import { useEffect, useState } from "react";
import { Loader2, CalendarPlus, Repeat } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { TripDetail } from "./TripDetail";
import { ChargeByCode } from "./ChargeReservation";
import { api, apiError } from "../../lib/api";

const iso = (d) => d.toISOString().slice(0, 10);
const DAYS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];
const LABEL = { programado: "Programado", a_la_venta: "A la venta", cerrado: "Venta cerrada", cancelado: "Cancelado", finalizado: "Realizado" };

const Generate = ({ routes, units, onDone }) => {
  const today = new Date();
  const [g, setG] = useState({ route_id: "", departure: "15:25", from_date: iso(today), to_date: iso(new Date(Date.now() + 29 * 864e5)), weekdays: [0, 1, 2, 3, 4, 5, 6], unit_id: "", on_sale: true });
  const go = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/trips/generate", { ...g, unit_id: g.unit_id || null });
      toast.success(`${data.created} salidas creadas`);
      onDone();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <form onSubmit={go} data-testid="trips-generate" className="grid gap-3 rounded-2xl bg-[var(--forest)] p-5 text-white md:grid-cols-4">
      <p className="flex items-center gap-2 font-display-up text-lg md:col-span-4"><CalendarPlus className="h-5 w-5 text-[var(--lime)]" /> Programar salidas</p>
      <select required data-testid="gen-route" value={g.route_id} onChange={(e) => setG({ ...g, route_id: e.target.value })} className="h-10 rounded-lg px-2 text-[var(--ink)] md:col-span-2"><option value="">Recorrido…</option>{routes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
      <Input data-testid="gen-time" type="time" required value={g.departure} onChange={(e) => setG({ ...g, departure: e.target.value })} className="h-10 text-[var(--ink)]" />
      <select data-testid="gen-unit" value={g.unit_id} onChange={(e) => setG({ ...g, unit_id: e.target.value })} className="h-10 rounded-lg px-2 text-[var(--ink)]"><option value="">Unidad (después)</option>{units.map((u) => <option key={u.id} value={u.id}>Interno {u.interno}</option>)}</select>
      <Input type="date" data-testid="gen-from" value={g.from_date} onChange={(e) => setG({ ...g, from_date: e.target.value })} className="h-10 text-[var(--ink)]" />
      <Input type="date" data-testid="gen-to" value={g.to_date} onChange={(e) => setG({ ...g, to_date: e.target.value })} className="h-10 text-[var(--ink)]" />
      <div className="flex flex-wrap gap-1 md:col-span-2">
        {DAYS.map((d, i) => <button type="button" key={d} onClick={() => setG({ ...g, weekdays: g.weekdays.includes(i) ? g.weekdays.filter((x) => x !== i) : [...g.weekdays, i] })} className={`h-10 w-10 rounded-lg text-xs font-bold ${g.weekdays.includes(i) ? "bg-[var(--lime)] text-[var(--ink)]" : "bg-white/10"}`}>{d}</button>)}
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={g.on_sale} onChange={(e) => setG({ ...g, on_sale: e.target.checked })} className="h-4 w-4" /> Poner a la venta</label>
      <button data-testid="gen-submit" className="h-10 rounded-lg bg-[var(--lime)] font-bold text-[var(--ink)] md:col-span-3">Crear salidas</button>
    </form>
  );
};

const Crew = ({ info, staff, onSaved }) => {
  const [crew, setCrew] = useState(() => Object.fromEntries(info.unit_ids.map((u) => [u, [info.drivers?.[u]?.[0] || "", info.drivers?.[u]?.[1] || ""]])));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/rotation/drivers", { drivers: Object.fromEntries(Object.entries(crew).map(([u, ids]) => [u, ids.filter(Boolean)])) });
      toast.success(`Choferes guardados · ${data.changed} salidas actualizadas`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const set = (u, k, v) => setCrew({ ...crew, [u]: crew[u].map((x, i) => (i === k ? v : x)) });
  return (
    <div data-testid="rotation-crew" className="grid w-full min-w-0 grid-cols-1 gap-3 border-t border-black/5 pt-4 md:grid-cols-[1fr_1fr_auto]">
      {info.unit_ids.map((u, n) => (
        <div key={u} className="min-w-0 space-y-1">
          <p className="text-xs font-bold uppercase tracking-wide text-[var(--muted-ink)]">Choferes fijos · Int. {info.units[n]}</p>
          <div className="flex gap-2">
            {[0, 1].map((k) => (
              <select key={k} data-testid={`crew-${info.units[n]}-${k}`} value={crew[u][k]} onChange={(e) => set(u, k, e.target.value)} className="h-10 min-w-0 flex-1 rounded-lg border border-black/10 px-2 text-sm">
                <option value="">{k === 0 ? "Chofer 1…" : "Chofer 2…"}</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            ))}
          </div>
        </div>
      ))}
      <button type="button" onClick={save} disabled={busy} data-testid="crew-save" className="h-10 self-end rounded-lg bg-[var(--ink)] px-4 text-sm font-bold text-white disabled:opacity-60">Guardar choferes</button>
      <p className="text-xs text-[var(--muted-ink)] md:col-span-3">Se asignan solos a cada salida del coche (próximos 120 días). Las salidas donde ya elegiste otros choferes a mano no se tocan.</p>
    </div>
  );
};

const Rotation = ({ staff, onDone }) => {
  const [info, setInfo] = useState(null);
  const [r, setR] = useState({ from_date: iso(new Date()), to_date: iso(new Date(Date.now() + 59 * 864e5)) });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/rotation").then((x) => setInfo(x.data)).catch(() => {}); }, []);
  if (!info?.configured) return null;
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/admin/rotation/apply", r); toast.success(`${data.changed} salidas creadas o reasignadas`); onDone(); } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} data-testid="rotation-form" className="flex flex-wrap items-end gap-3 rounded-2xl bg-white p-5 ring-1 ring-black/5">
      <div className="min-w-[220px] flex-1">
        <p className="flex items-center gap-2 font-semibold"><Repeat className="h-4 w-4 text-[var(--forest)]" /> Rotación de coches</p>
        <p data-testid="rotation-info" className="mt-1 text-sm text-[var(--muted-ink)]">Int. {info.units.join(" e Int. ")} salen día por medio (ida y vuelta al día siguiente). Int. {info.reserve} queda de reserva.</p>
      </div>
      <Input type="date" data-testid="rotation-from" value={r.from_date} onChange={(e) => setR({ ...r, from_date: e.target.value })} className="h-10 w-auto" />
      <Input type="date" data-testid="rotation-to" value={r.to_date} onChange={(e) => setR({ ...r, to_date: e.target.value })} className="h-10 w-auto" />
      <button disabled={busy} data-testid="rotation-apply" className="h-10 rounded-lg bg-[var(--forest)] px-4 text-sm font-bold text-white disabled:opacity-60">Programar recorridos con rotación</button>
      <Crew info={info} staff={staff} onSaved={onDone} />
    </form>
  );
};

export const TripsPanel = ({ units, staff, isAdmin }) => {
  const [routes, setRoutes] = useState([]);
  const [range, setRange] = useState([iso(new Date()), iso(new Date(Date.now() + 13 * 864e5))]);
  const [trips, setTrips] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/trips", { params: { date_from: range[0], date_to: range[1] } }).then((r) => setTrips(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  useEffect(() => { if (isAdmin) api.get("/admin/routes").then((r) => setRoutes(r.data ?? [])).catch(() => {}); }, [isAdmin]);
  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="trips-panel" className="space-y-5">
      {isAdmin && <Rotation staff={Array.isArray(staff) ? staff : []} onDone={load} />}
      {isAdmin && <Generate routes={routes} units={units} onDone={load} />}
      <ChargeByCode onChanged={load} />
      <div className="flex flex-wrap items-center gap-2 text-sm">
        Desde <Input type="date" value={range[0]} onChange={(e) => setRange([e.target.value, range[1]])} className="h-9 w-auto" /> hasta <Input type="date" value={range[1]} onChange={(e) => setRange([range[0], e.target.value])} className="h-9 w-auto" />
      </div>
      {trips === null ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-black/5">
          <table className="w-full text-sm">
            <thead className="bg-[var(--paper)] text-left font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--muted-ink)]"><tr><th className="p-3">Fecha</th><th className="p-3">Hora</th><th className="p-3">Recorrido</th><th className="p-3">Unidad</th><th className="p-3">Conductores</th><th className="p-3">Vendidos</th><th className="p-3">Estado</th></tr></thead>
            <tbody>
              {trips.length === 0 && <tr><td colSpan={7} className="p-6 text-[var(--muted-ink)]">No hay salidas en estas fechas.</td></tr>}
              {trips.map((t) => (
                <tr key={t.id} onClick={() => setOpen(t.id)} data-testid={`trip-row-${t.id}`} className="cursor-pointer border-t border-black/5 hover:bg-[var(--paper)]">
                  <td className="p-3 font-mono">{t.date}</td><td className="p-3 font-mono">{t.departure}</td><td className="p-3">{t.route_name}</td>
                  <td className={`p-3 ${t.unit_label ? "" : "font-semibold text-amber-700"}`}>{t.unit_label ? `Int. ${t.unit_label}` : "Falta"}</td>
                  <td className={`p-3 ${t.drivers.length ? "" : "font-semibold text-amber-700"}`}>{t.drivers.length ? t.drivers.join(", ") : "Falta asignar"}</td>
                  <td className="p-3">{t.sold}</td><td className="p-3"><span className="rounded-full bg-[var(--paper)] px-2.5 py-1 text-xs font-bold">{LABEL[t.status] ?? t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TripDetail tripId={open} units={units} staff={staff} isAdmin={isAdmin} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
};
